import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  Spinner,
  Pagination,
  usePagination,
  Link,
  SubTitle,
  Text,
  Tooltip,
  Icon,
} from '@appcues/sonar';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons/faQuestionCircle';
import NoData from 'next/assets/images/no-data.svg';
import { getPropertyDisplayName } from 'next/lib/profile-attributes';
import { Shape as SurveyShape } from 'next/entities/surveys';
import { formatDate } from 'utils';
import AutoGeneratedIdAlert from './AutoGeneratedIdAlert';
import PreventSort from './PreventSort';
import CustomFieldInfoButton from './CustomFieldInfoButton';
import {
  Cell,
  HeaderCell,
  HeaderWrapper,
  LoadingWrapper,
  NoResultsMessage,
  TableHeaderContentWrapper,
  TableHeaderLabelWrapper,
  TextWrapper,
  Wrapper,
} from './styled';
import { sortResponses } from './sort-responses';

/**
 * Regex to match the patter "word-UUIDv4" used for auto-generated survey labels. Regex Explanation:
 * \b              // Asserts a word boundary
 * \w+             // Matches one or more word characters (letters, digits, or underscores)
 * -               // Matches a dash
 * [\da-f]{8}     // Matches exactly 8 characters from 0-9 or a-f (UUIDv4 format)
 * -               // Matches a dash
 * 4               // Matches the character '4'
 * [\da-f]{3}     // Matches exactly 3 characters from 0-9 or a-f (UUIDv4 format)
 * -               // Matches a dash
 * [89ab]          // Matches a character from '89ab'
 * [\da-f]{3}     // Matches exactly 3 characters from 0-9 or a-f (UUIDv4 format)
 * -               // Matches a dash
 * [\da-f]{12}    // Matches exactly 12 characters from 0-9 or a-f (UUIDv4 format)
 * \b              // Asserts a word boundary
 */
export const autoGeneratedIdFormat =
  /\b\w+-[\da-f]{8}-[\da-f]{4}-4[\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}\b/i;

const transformRows = answers =>
  answers.map(({ fieldAnswers, userId, timestamp, userProfileAttributes }) => ({
    userId,
    timestamp,
    ...fieldAnswers.reduce(
      (acc, { fieldId, value }) => ({
        ...acc,
        [fieldId.replaceAll("'", '')]: value,
      }),
      {}
    ),
    ...Object.entries(userProfileAttributes).reduce(
      (acc, [attribute, value]) => ({
        ...acc,
        [attribute]: value || '-',
      }),
      {}
    ),
  }));

const renderColumnElement = (values, columnId, identifier) => {
  switch (columnId) {
    case 'userId':
    case identifier:
      return (
        <Link href={`/audience/users/${values.userId}/properties`}>
          {values[columnId]}
        </Link>
      );
    case 'timestamp':
      return formatDate(
        new Date(values[columnId]).getTime(),
        'MMM D, YYYY h:mm A'
      );
    default:
      return values[columnId];
  }
};

export const SurveyResponses = ({
  flowId,
  surveys = {},
  onLoad,
  currentIdentifier,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [sortConfig, setSortConfig] = useState({
    column: 'timestamp',
    direction: 'DESC',
  });

  const handleSort = column => {
    setSortConfig(prevState => {
      if (prevState?.column === column) {
        return {
          column,
          direction: prevState.direction === 'ASC' ? 'DESC' : 'ASC',
        };
      }
      return {
        column,
        direction: 'ASC',
      };
    });
  };

  useEffect(() => {
    const load = async () => {
      await onLoad?.(currentIdentifier);
      setLoading(false);
    };

    load();
    return () => setLoading(false);
  }, [currentIdentifier, onLoad]);

  const { pageIndex, pageSize, setPagination } = usePagination();

  const columns = useMemo(
    () => [
      {
        key: currentIdentifier,
        label: getPropertyDisplayName(currentIdentifier),
      },
      ...(surveys?.labels || []).map(
        ({ fieldId: key, label, customReportingLabel, stepChildId }) => ({
          key,
          label: customReportingLabel || label,
          stepChildId,
        })
      ),
      { key: 'timestamp', label: 'Date received' },
    ],
    [currentIdentifier, surveys?.labels]
  );

  const transformedRows = useMemo(
    () => transformRows(surveys?.answers || []),
    [surveys?.answers]
  );

  const sortedRows = useMemo(
    () => [...transformedRows].sort(sortResponses(sortConfig)),
    [transformedRows, sortConfig]
  );

  // Pagination is handled on the client side because the survey responses query takes the
  // same amount of time regardless of how many responses are requested.
  const rows = useMemo(
    () =>
      sortedRows.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [sortedRows, pageIndex, pageSize]
  );

  const firstAutoGeneratedIdColumn =
    rows.length > 0 &&
    columns.find(({ label }) => label?.match(autoGeneratedIdFormat));
  return (
    <Wrapper>
      <HeaderWrapper>
        <SubTitle>Survey Responses</SubTitle>
      </HeaderWrapper>
      {surveys?.totalAnswersInRangeCount &&
      surveys.totalAnswersInRangeCount > 1000 ? (
        <TextWrapper>
          <Text weight="bold">1000</Text>{' '}
          <Text>
            of {surveys.totalAnswersInRangeCount.toLocaleString()} responses
          </Text>
          <Tooltip
            content={
              <Text
                size="small"
                lineHeight="large"
                colorToken="foreground-tooltip"
              >
                Export your survey data to see all{' '}
                {surveys.totalAnswersInRangeCount.toLocaleString()} responses.
              </Text>
            }
          >
            <Icon icon={faQuestionCircle} colorToken="foreground-link" />
          </Tooltip>
        </TextWrapper>
      ) : null}

      {firstAutoGeneratedIdColumn && (
        <AutoGeneratedIdAlert
          flowId={flowId}
          stepChildId={firstAutoGeneratedIdColumn.stepChildId}
        />
      )}

      {loading ? (
        <LoadingWrapper>
          <Spinner />
          <Text>Loading your responses...this might take a minute.</Text>
        </LoadingWrapper>
      ) : !surveys?.answers ? (
        <NoResultsMessage>
          <img src={NoData} alt="No data found" />
          Failed to load survey responses.
        </NoResultsMessage>
      ) : surveys?.answers.length === 0 ? (
        <NoResultsMessage>
          <img src={NoData} alt="No data found" />
          {surveys?.totalAnswersCount > 0
            ? 'No survey responses for the selected dates. Adjust date range selection to view responses.'
            : 'No survey responses yet.'}
        </NoResultsMessage>
      ) : (
        <Table.Container>
          <Table.Root>
            <Table.Thead>
              <Table.Tr>
                {columns.map(({ key, label, stepChildId }) => (
                  <HeaderCell
                    sortable
                    sortDirection={
                      sortConfig.column === key
                        ? sortConfig?.direction
                        : undefined
                    }
                    onClick={() => handleSort(key)}
                    key={key}
                  >
                    <TableHeaderContentWrapper>
                      <TableHeaderLabelWrapper title={label}>
                        {label}
                      </TableHeaderLabelWrapper>
                      <PreventSort>
                        {label?.match(autoGeneratedIdFormat) && (
                          <CustomFieldInfoButton
                            flowId={flowId}
                            stepChildId={stepChildId}
                          />
                        )}
                      </PreventSort>
                    </TableHeaderContentWrapper>
                  </HeaderCell>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(values => (
                <Table.Tr key={values.userId + values.timestamp}>
                  {columns.map(column => (
                    <Cell key={column.key + values.userId}>
                      {renderColumnElement(
                        values,
                        column.key,
                        currentIdentifier
                      )}
                    </Cell>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table.Root>
          <Table.Footer>
            <Pagination
              totalResults={(surveys?.answers || []).length}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPagination={setPagination}
            />
          </Table.Footer>
        </Table.Container>
      )}
    </Wrapper>
  );
};

SurveyResponses.propTypes = {
  flowId: PropTypes.string,
  surveys: SurveyShape,
  onLoad: PropTypes.func,
  currentIdentifier: PropTypes.string,
};

export default SurveyResponses;
